// Org-admin settings tab — edit organisation name, ABN, contact info,
// address, the org-level performance grade threshold offset that shifts
// every contract's A-E grade boundaries uniformly, and the overdue-alert
// delivery cadence (per-item once / weekly / fortnightly / daily digest).

import { useState } from 'react';
import { useCurrentOrganisation, useUpdateCurrentOrganisation } from '../../hooks/useCurrentOrganisation';
import { Button, LoadingSpinner, Alert } from '../../components/common';
import type { OverdueAlertMode } from '../../types';

const DEFAULT_OVERDUE_ALERT_MODE: OverdueAlertMode = 'per-item-weekly';

const OFFSET_MIN = -30;
const OFFSET_MAX = 10;

function parseOffsetInput(raw: string): { value: number | null; error: string | null } {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, error: null };
  if (!/^-?\d+$/.test(trimmed)) {
    return { value: null, error: 'Offset must be a whole number' };
  }
  const n = Number(trimmed);
  if (n < OFFSET_MIN || n > OFFSET_MAX) {
    return { value: null, error: `Offset must be between ${OFFSET_MIN} and ${OFFSET_MAX}` };
  }
  return { value: n, error: null };
}

export default function OrgSettingsTab() {
  const { data: org, isLoading } = useCurrentOrganisation();
  const updateMutation = useUpdateCurrentOrganisation();

  const [name, setName] = useState('');
  const [abn, setAbn] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');
  const [gradeOffset, setGradeOffset] = useState('');
  const [overdueAlertMode, setOverdueAlertMode] = useState<OverdueAlertMode>(
    DEFAULT_OVERDUE_ALERT_MODE,
  );

  // Pattern 0: seed form fields during render when the org data loads or
  // changes identity (e.g. after a save + query invalidation). Guard on
  // org.id so the form does not re-seed while the user is typing.
  const orgId = org?.id ?? null;
  const [seededForOrgId, setSeededForOrgId] = useState<string | null>(null);
  if (orgId !== null && orgId !== seededForOrgId) {
    setSeededForOrgId(orgId);
    setName(org!.name || '');
    setAbn(org!.abn || '');
    setContactEmail(org!.contactEmail || '');
    setContactPhone(org!.contactPhone || '');
    setAddress(org!.address || '');
    setGradeOffset(
      org!.performanceGradeThresholdOffset === null ||
        org!.performanceGradeThresholdOffset === undefined
        ? ''
        : String(org!.performanceGradeThresholdOffset),
    );
    setOverdueAlertMode(org!.overdueAlertMode ?? DEFAULT_OVERDUE_ALERT_MODE);
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const offsetParse = parseOffsetInput(gradeOffset);
  const offsetError = offsetParse.error;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (offsetError) return;
    await updateMutation.mutateAsync({
      name,
      abn: abn || undefined,
      contactEmail: contactEmail || undefined,
      contactPhone: contactPhone || undefined,
      address: address || undefined,
      performanceGradeThresholdOffset: offsetParse.value,
      overdueAlertMode,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-lg space-y-4">
      <div>
        <label htmlFor="org-name" className="label">Name</label>
        <input id="org-name" type="text" value={name} onChange={(e) => setName(e.target.value)} required className="input" />
      </div>
      <div>
        <label htmlFor="org-abn" className="label">ABN</label>
        <input id="org-abn" type="text" value={abn} onChange={(e) => setAbn(e.target.value)} className="input" />
      </div>
      <div>
        <label htmlFor="org-contact-email" className="label">Contact Email</label>
        <input id="org-contact-email" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="input" />
      </div>
      <div>
        <label htmlFor="org-contact-phone" className="label">Contact Phone</label>
        <input id="org-contact-phone" type="text" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="input" />
      </div>
      <div>
        <label htmlFor="org-address" className="label">Address</label>
        <textarea id="org-address" value={address} onChange={(e) => setAddress(e.target.value)} rows={2} className="input" />
      </div>
      <div>
        <label htmlFor="org-grade-offset" className="label">Performance grade threshold offset</label>
        <input
          id="org-grade-offset"
          type="number"
          inputMode="numeric"
          step={1}
          min={OFFSET_MIN}
          max={OFFSET_MAX}
          value={gradeOffset}
          onChange={(e) => setGradeOffset(e.target.value)}
          aria-describedby="org-grade-offset-help"
          aria-invalid={offsetError !== null}
          className="input"
          data-testid="org-grade-offset-input"
        />
        <p
          id="org-grade-offset-help"
          className="mt-1 text-caption text-slate-500 dark:text-slate-400"
        >
          Adjusts all A-E grade boundaries uniformly across every contract.
          Negative = looser (lower scores still earn a higher letter).
          Positive = stricter. Range {OFFSET_MIN} to {OFFSET_MAX}. Leave
          blank to use the system defaults.
        </p>
        {offsetError && (
          <p
            data-testid="org-grade-offset-error"
            className="mt-1 text-caption text-red-700 dark:text-red-300"
          >
            {offsetError}
          </p>
        )}
      </div>
      <div>
        <label htmlFor="org-overdue-alert-mode" className="label">
          Overdue alert delivery
        </label>
        <select
          id="org-overdue-alert-mode"
          value={overdueAlertMode}
          onChange={(e) =>
            setOverdueAlertMode(e.target.value as OverdueAlertMode)
          }
          aria-describedby="org-overdue-alert-mode-help"
          className="input"
        >
          <option value="per-item-once">Once per item</option>
          <option value="per-item-weekly">Weekly reminder</option>
          <option value="per-item-fortnightly">Fortnightly reminder</option>
          <option value="digest-daily">Daily digest</option>
        </select>
        <p
          id="org-overdue-alert-mode-help"
          className="mt-1 text-caption text-slate-500 dark:text-slate-400"
        >
          Controls reminders for overdue deliverables and milestones.
          Due-soon alerts are unchanged.
        </p>
      </div>
      <Button
        type="submit"
        disabled={!name.trim() || offsetError !== null}
        loading={updateMutation.isPending}
      >
        Save Changes
      </Button>
      {updateMutation.isSuccess && <Alert variant="success">Changes saved.</Alert>}
    </form>
  );
}
